import { useState, useEffect } from "react";
import { useLocation, Form } from "react-router";
import { Globe, X } from "lucide-react";
import { LOCALES } from "~/i18n/index";
import type { Locale } from "~/i18n/index";
import { useT } from "~/i18n/context";

const MARKETS: Record<Locale, { short: string; language: string; market: string; region: string }> = {
  en: { short: "EN", language: "English", market: "Global", region: "Europe & International" },
  "zh-HK": { short: "繁中", language: "繁體中文", market: "Hong Kong", region: "Asia Pacific" },
  "zh-TW": { short: "繁中", language: "繁體中文", market: "Taiwan", region: "Asia Pacific" },
  "zh-CN": { short: "简中", language: "简体中文", market: "Mainland China", region: "Asia Pacific" },
  ja: { short: "日本語", language: "日本語", market: "Japan", region: "Asia Pacific" },
  ko: { short: "한국어", language: "한국어", market: "South Korea", region: "Asia Pacific" },
  th: { short: "ไทย", language: "ไทย", market: "Thailand", region: "Asia Pacific" },
  ru: { short: "RU", language: "Русский", market: "Russia & CIS", region: "Europe & CIS" },
};

/**
 * Global locale selector — positions Caratzon as a multi-market
 * international platform, not just a language toggle.
 */
export function LanguageSwitcher() {
  const { locale, t } = useT();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open]);

  const current = MARKETS[locale];

  return (
    <>
      {/* Trigger — shows current market */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex items-center gap-[0.3rem] uppercase"
        style={{
          fontFamily: "var(--font-sans)",
          fontSize: "0.58rem",
          letterSpacing: "0.18em",
          background: "none",
          border: "none",
          cursor: "pointer",
          color: "inherit",
          opacity: 0.5,
          padding: "0.25rem 0",
          lineHeight: 1,
        }}
        aria-haspopup="dialog"
        aria-label={`Market: ${current.market}`}
      >
        <Globe size={12} strokeWidth={1.5} />
        <span>{current.short}</span>
      </button>

      {/* Full-screen overlay */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Select your market"
        className="fixed inset-0 z-[100] transition-all duration-300"
        style={{
          opacity: open ? 1 : 0,
          pointerEvents: open ? "auto" : "none",
        }}
      >
        {/* Backdrop */}
        <div
          className="absolute inset-0"
          style={{ background: "oklch(0.08 0.005 50 / 0.65)", backdropFilter: "blur(16px)" }}
          onClick={() => setOpen(false)}
        />

        {/* Content */}
        <div
          className="relative z-10 flex flex-col items-center justify-center min-h-full px-6"
          style={{
            transform: open ? "scale(1)" : "scale(0.96)",
            transition: "transform 0.3s cubic-bezier(0.16, 1, 0.3, 1)",
          }}
        >
          <div
            className="w-full"
            style={{
              maxWidth: "520px",
              background: "oklch(0.978 0.008 90)",
              borderRadius: "8px",
              boxShadow: "0 24px 80px oklch(0 0 0 / 0.3)",
              overflow: "hidden",
            }}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 pt-6 pb-2">
              <span
                className="font-display block"
                style={{ fontSize: "1.3rem", fontWeight: 300, color: "oklch(0.085 0.004 50)" }}
              >
                Choose Your Market
              </span>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="flex items-center justify-center w-9 h-9 -mr-1 rounded-full transition-colors"
                style={{ color: "oklch(0.47 0.008 55)" }}
                aria-label="Close"
              >
                <X size={18} strokeWidth={1.5} />
              </button>
            </div>

            {/* Thin rule */}
            <div className="mx-6 mt-3" style={{ height: "1px", background: "oklch(0.82 0.008 75)" }} />

            {/* Market options */}
            <div className="px-3 py-3">
              {LOCALES.map((loc: Locale) => {
                const m = MARKETS[loc];
                const isActive = locale === loc;
                return (
                  <Form
                    key={loc}
                    method="post"
                    action="/resources/set-language"
                    reloadDocument
                  >
                    <input type="hidden" name="locale" value={loc} />
                    <input type="hidden" name="returnTo" value={pathname} />
                    <button
                      type="submit"
                      disabled={isActive}
                      className="flex items-center gap-5 w-full text-left px-4 py-4 rounded-md transition-colors"
                      style={{
                        background: isActive ? "oklch(0.45 0.08 213 / 0.07)" : "transparent",
                        cursor: isActive ? "default" : "pointer",
                        border: "none",
                      }}
                      onMouseEnter={(e) => {
                        if (!isActive) (e.currentTarget as HTMLElement).style.background = "oklch(0.95 0.005 80)";
                      }}
                      onMouseLeave={(e) => {
                        if (!isActive) (e.currentTarget as HTMLElement).style.background = isActive ? "oklch(0.45 0.08 213 / 0.07)" : "transparent";
                      }}
                    >
                      {/* Globe marker */}
                      <div
                        className="flex items-center justify-center shrink-0"
                        style={{
                          width: "40px",
                          height: "40px",
                          borderRadius: "50%",
                          background: isActive ? "oklch(0.45 0.08 213 / 0.12)" : "oklch(0.93 0.005 80)",
                        }}
                      >
                        <Globe
                          size={18}
                          strokeWidth={1.2}
                          style={{ color: isActive ? "oklch(0.45 0.08 213)" : "oklch(0.47 0.008 55)" }}
                        />
                      </div>

                      {/* Market info */}
                      <div className="flex-1 min-w-0">
                        <span
                          className="font-display block"
                          style={{
                            fontSize: "1.1rem",
                            fontWeight: isActive ? 500 : 300,
                            color: isActive ? "oklch(0.45 0.08 213)" : "oklch(0.085 0.004 50)",
                          }}
                        >
                          {m.market}
                        </span>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span
                            className="uppercase"
                            style={{
                              fontFamily: "var(--font-sans)",
                              fontSize: "0.54rem",
                              letterSpacing: "0.14em",
                              color: isActive ? "oklch(0.45 0.08 213 / 0.6)" : "oklch(0.47 0.008 55 / 0.6)",
                            }}
                          >
                            {m.language}
                          </span>
                          <span style={{ color: "oklch(0.82 0.008 75)", fontSize: "0.5rem" }}>·</span>
                          <span
                            className="uppercase"
                            style={{
                              fontFamily: "var(--font-sans)",
                              fontSize: "0.54rem",
                              letterSpacing: "0.14em",
                              color: "oklch(0.47 0.008 55 / 0.4)",
                            }}
                          >
                            {m.region}
                          </span>
                        </div>
                      </div>

                      {/* Active indicator */}
                      {isActive && (
                        <span
                          className="uppercase shrink-0"
                          style={{
                            fontFamily: "var(--font-sans)",
                            fontSize: "0.5rem",
                            letterSpacing: "0.16em",
                            color: "oklch(0.45 0.08 213)",
                            padding: "0.25rem 0.5rem",
                            border: "1px solid oklch(0.45 0.08 213 / 0.2)",
                            borderRadius: "3px",
                          }}
                        >
                          Active
                        </span>
                      )}
                    </button>
                  </Form>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
